<script setup lang="ts">
import FsLightbox from "fslightbox-vue/v3";
import { storeToRefs } from "pinia";
import { useProductPageStore } from "~/stores/main";
import breakpoints from "#twcss/theme/screens";

const { t } = useI18n();
const defaultStore = useProductPageStore();
const route = useRoute();
const { getItem, setItem } = useSessionData();
const { masterProductImageAltTag } = useHelpers();

const props = withDefaults(
  defineProps<{
    images: string[];
    store?: any;
  }>(),
  {
    store: null,
  },
);

// Use injected store or fallback to default
const store = props.store || defaultStore;
const { currentImageIndex } = storeToRefs(store);

// -----------------------
// refs
// -----------------------
const sessionKey = `product-${route.params.sku}`;
const sessionData = (await getItem(sessionKey)) || {};
const imageHeight = ref<number>(0); // $menu-height
const containerTop = ref<number>(sessionData["container-top"] || 0);
const maximumYPosition = ref<number>(sessionData["maximum-y-position"] || 0);
const headerHeight = ref<number>(56);
const lightboxTogglerRef = ref<boolean>(false);
const currentSlideRef = ref<number>(1);
const followScroll = ref(sessionData["follow-scroll"] || false);
const reachedEnd = ref(sessionData["reached-end"] || false);

// -----------------------
// computed properties
// -----------------------
const floatingGallery = computed(() => {
  return !reachedEnd.value && followScroll.value;
});

// -----------------------
// helper functions
// -----------------------
const openLightBox = (index: number) => {
  if (window.innerWidth < parseInt(breakpoints.lg)) {
    return;
  }

  lightboxTogglerRef.value = !lightboxTogglerRef.value;
  currentSlideRef.value = index;
};

const getContainerElement = () => {
  return document.querySelector(".product-container .column-left");
};

const updateFollowScroll = () => {
  if (
    maximumYPosition.value === 0 ||
    window.innerWidth < parseInt(breakpoints.lg)
  ) {
    return;
  }

  const containerElement = getContainerElement();

  if (!containerElement) {
    return;
  }

  const scrollTop =
    window.scrollY ||
    document.body.scrollTop ||
    document.documentElement.scrollTop;
  const parent = containerElement.parentElement;
  const paddingBottom = parseFloat(getComputedStyle(parent).paddingBottom);

  if (
    scrollTop >
    maximumYPosition.value -
      imageHeight.value -
      headerHeight.value -
      paddingBottom
  ) {
    reachedEnd.value = true;
    return;
  } else {
    reachedEnd.value = false;
  }

  followScroll.value =
    scrollTop + headerHeight.value + paddingBottom >= containerTop.value;
};

const calculateDimensions = () => {
  const containerElement = getContainerElement();

  if (!(containerElement instanceof HTMLElement)) {
    return;
  }

  const parent = containerElement?.parentElement;

  if (!(parent instanceof HTMLElement)) {
    return;
  }

  const parentTop = parent.offsetTop;
  const parentHeight = parent.offsetHeight;
  const paddingTop = parseFloat(getComputedStyle(parent).paddingTop);
  const paddingBottom = parseFloat(getComputedStyle(parent).paddingBottom);
  const imageContainer = parent.querySelector(".image-container");

  if (imageContainer instanceof HTMLElement) {
    imageHeight.value = imageContainer.offsetHeight;
  }

  containerTop.value = parentTop + paddingTop;
  maximumYPosition.value = parentTop + parentHeight - paddingBottom;
};

const imageLoaded = () => {
  calculateDimensions();
  updateFollowScroll();

  window.addEventListener("resize", () => {
    if (window.innerWidth < parseInt(breakpoints.lg)) {
      reachedEnd.value = false;
      followScroll.value = false;
      return;
    }

    setTimeout(() => {
      calculateDimensions();
      updateFollowScroll();
    }, 100);
  });
};

// -----------------------
// vue events
// -----------------------
let lastSessionUpdateInitiated = 0;

watch([followScroll, reachedEnd, containerTop, maximumYPosition], () => {
  // Avoid too frequent session updates
  lastSessionUpdateInitiated = Date.now();

  setTimeout(() => {
    if (Date.now() - lastSessionUpdateInitiated >= 1000) {
      sessionData["follow-scroll"] = followScroll.value;
      sessionData["reached-end"] = reachedEnd.value;
      sessionData["container-top"] = containerTop.value;
      sessionData["maximum-y-position"] = maximumYPosition.value;
      setItem(sessionKey, sessionData);
    }
  }, 1000);
});

if (import.meta.client) {
  onMounted(() => {
    calculateDimensions();
    window.addEventListener("scroll", updateFollowScroll);
  });
}
</script>

<template>
  <div v-if="floatingGallery" class="column-left hidden lg:block">
    <!-- Empty div to retain two cols in grid -->
  </div>
  <div
    class="w-full hidden lg:block gallery-container"
    :class="{
      fixed: floatingGallery,
      'column-left': !floatingGallery,
      'place-self-end': reachedEnd,
    }"
    :style="{
      top: `${headerHeight + 20}px`,
    }"
  >
    <div
      class="image-container relative w-full rounded-lg p-5 aspect-square"
      :class="{ 'bg-white': images.length > 0, 'follow-scroll': followScroll }"
    >
      <!-- Placeholder image -->
      <img
        v-if="images.length === 0"
        src="/images/Image_placeholder.svg"
        class="image-placeholder row-start-1 row-end-1 col-start-1 col-end-2 mx-auto"
        :alt="t('product_page.images_loading')"
      />

      <!-- (Current) Product image -->
      <NuxtImg
        v-if="images.length > 0"
        v-touch:swipe.left="store.nextImage"
        v-touch:swipe.right="store.previousImage"
        :src="images[currentImageIndex]"
        format="webp"
        preload
        class="mx-auto rounded-lg w-auto h-full"
        :width="700"
        @click="openLightBox(currentImageIndex + 1)"
        @load="imageLoaded"
        :alt="masterProductImageAltTag(store.masterProduct)"
      ></NuxtImg>

      <!-- Left/right arrows -->
      <button
        v-if="images.length > 0 && currentImageIndex > 0"
        class="flex justify-center items-center w-[34px] h-[34px] absolute left-1.5 top-1/2 -mt-[17px] bg-brand-secondary rounded-full shadow-md"
        @click="store.previousImage"
      >
        <i class="m-icon-arrow-left text-xl ml-[-3px] text-brand-headline"></i>
      </button>
      <button
        v-if="images.length > 0 && currentImageIndex < images.length - 1"
        class="flex justify-center items-center w-[34px] h-[34px] absolute right-1.5 top-1/2 -mt-[17px] bg-brand-secondary rounded-full shadow-md"
        @click="store.nextImage"
      >
        <i class="m-icon-arrow-right text-xl ml-[3px] text-brand-headline"></i>
      </button>

      <!-- Index -->
      <div
        v-if="images.length > 0"
        class="absolute right-3 bottom-2 font-bold text-sm"
      >
        {{ currentImageIndex + 1 }}/{{ images.length }}
      </div>
    </div>
  </div>
  <FsLightbox
    v-if="images.length > 0"
    :toggler="lightboxTogglerRef"
    :sources="images"
    :slide="currentSlideRef"
  />
</template>

<style lang="scss" scoped>
@keyframes loading-effect {
  0% {
    opacity: 0.5;
  }

  100% {
    opacity: 1;
  }
}

$gallery-container-width: calc(
  calc(min(calc(100vw - 14px), 1920px) - 100px) / 2
);

.image-placeholder {
  animation-name: loading-effect;
  animation-duration: 500ms;
  animation-iteration-count: infinite;
  animation-direction: alternate;
}

.fixed.gallery-container {
  width: $gallery-container-width;
}

@media (max-height: 895px) {
  .image-container {
    height: min(
      calc(
        100vh - 56px /* $menu-height */ - 60px /* $breadcrum-height */ - 40px
          /* $gallery-padding */
      ),
      $gallery-container-width
    );
  }

  .image-container.follow-scroll {
    height: min(
      calc(100vh - 56px /* $menu-height */ - 40px /* $gallery-padding */),
      $gallery-container-width
    );
  }
}

@media (min-height: 896px) {
  .image-container {
    height: min(
      calc(700px /* $max-image-height */ + 40px /* $image-padding */),
      $gallery-container-width
    );
  }
}
</style>
